import getAttributes from '@/screens/workflows/components/workflowInputVariablePane/lib/getAttributes';
import getParentIterationStepIds from '@/screens/workflows/lib/getParentIterationStepIds';
import { getFullSlugFromStep } from '@shared/utils/getFullSlugFromStep.js';
import { setDeep } from '@shared/utils/setDeep.js';

const buildMapEditorInput = ({ stepId, inputSteps = [], apps, workflow, environment }) => {
  // THIS NEEDS INPUT NEEDS TO MATCH runMapStep.js INPUT
  const memory = getAttributes({
    stepId: 'memory',
    apps,
    workflow,
    output: undefined
  });

  const system = getAttributes({
    stepId: 'system',
    apps,
    workflow,
    output: undefined,
    environment
  });

  const workflowArgumentDefs = workflow?.meta?.arguments || [];
  const workflowArguments = getAttributes({
    stepId: 'workflowArguments',
    apps,
    workflow,
    workflowArgumentDefs,
    environment
  });

  const integrationMetadataDefs = workflow?.integrationMetadataDefinitions || [];
  const installationMetadata = workflow?.installationMetadata || {};
  const integrationMetadata = getAttributes({
    stepId: 'integrationMetadata',
    apps,
    workflow,
    integrationMetadataDefs,
    installationMetadata
  });

  const functionInputArgumentDefs = workflow?.steps?.find((n) => n?.appSlug === 'functionInputs')?.options?.inputArguments || [];
  const functionInputTestArguments = workflow?.steps?.find((n) => n?.appSlug === 'functionInputs')?.options?.testArguments || [];
  const functionInputs = getAttributes({
    stepId: 'functionInputs',
    apps,
    workflow,
    functionInputArgumentDefs,
    functionInputTestArguments
  });

  const payload = {};
  const inputStepNumbers = {};
  const inputStepNames = {};
  let hasLargeInput = false;

  inputSteps?.forEach((obj) => {
    let output = Array.isArray(obj.output?.data) ? obj.output?.data?.slice(0, 100) : obj.output?.data;
    const inputStepIdParents = obj.inputStepIdParents?.[obj.step?.id];
    const hasParents = inputStepIdParents?.length > 0;
    const fullSlug = getFullSlugFromStep({ step: obj.step, app: obj.app, action: obj.appAction, extra: hasParents ? 'Group' : '' });
    if (hasParents) {
      // Get active step parents and compare to know how much we need to nest. Steps might be at the same level or not.
      const stepParents = getParentIterationStepIds(stepId, workflow?.steps, apps, workflow?.graph);
      const repeatedParents = stepParents.filter((n) => inputStepIdParents.includes(n));
      const nestAmount = inputStepIdParents.length - repeatedParents.length;

      // We need to nest output according to how many parents there are, example: [ [ [ output ] ] ] (2 parents, output is already an array).
      let nestedOutput = output;

      for (let i = 0; i < nestAmount; i++) {
        nestedOutput = [nestedOutput];
      }

      output = nestedOutput;
    }
    payload[fullSlug] = output || [];
    inputStepNumbers[fullSlug] = obj.step?.stepNumber;
    inputStepNames[fullSlug] = obj.step?.message || '';

    if (obj?.output?.data?.length > 10 && obj?.output?.meta?.size > 10) {
      hasLargeInput = true;
    }
  });

  if (memory?.length > 0) {
    payload.memory = {};
    memory.forEach((n) => {
      const result = setDeep(payload.memory, n.key, n.outputs?.[0]);
      payload.memory = result;
    });
  }

  if (functionInputs?.length > 0) {
    payload.functionInputs = Object.fromEntries(functionInputs.map((n) => [n.key, n.value]));
  }

  if (workflowArguments?.length > 0) {
    payload.workflowArguments = Object.fromEntries(workflowArguments.map((n) => [n.key, n.value]));
  }

  if (integrationMetadata?.length > 0) {
    payload.integrationMetadata = Object.fromEntries(integrationMetadata.map((n) => [n.key, n.value]));
  }

  if (system?.length > 0) {
    payload.system = {};
    const systemCustom = system.filter((n) => n.category === 'custom');
    const systemDates = system.filter((n) => n.category === 'dates');
    const systemRun = system.filter((n) => n.category === 'run');
    if (systemCustom?.length > 0) {
      payload.system.custom = Object.fromEntries(systemCustom.map((n) => [n.key, n.outputs?.[0]]));
    }
    if (systemDates?.length > 0) {
      payload.system.dates = Object.fromEntries(systemDates.map((n) => [n.key, n.outputs?.[0]]));
    }
    if (systemRun?.length > 0) {
      payload.system.run = Object.fromEntries(systemRun.map((n) => [n.key, n.outputs?.[0]]));
    }
  }

  return { editorInput: payload, inputStepNumbers, inputStepNames, hasLargeInput };
};

export default buildMapEditorInput;
